import React, {PropsWithChildren, useCallback, useMemo, useState} from 'react';
import {Box, Grid, Link} from '@mui/material';
import {gql, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import Loading from '../Loading/Loading';
// import SendPortalNotificationModal from './SendPortalNotificationModal';
import SendAtrQuestionnaireModal from './SendAtrQuestionnaireModal';
import SendFactFindModal from './SendFactFindModal';
import {useAccountContext} from '../../contexts/AccountContext';
import {AccountContact} from '../../schema-types/AccountContact';
import {Panel} from '../Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {GetFinCalcUrl, GetFinCalcUrlVariables} from '../../schema-types/GetFinCalcUrl';
import RequestFPASignatureModal from './RequestFpaSignature';
import {useFastAccountContacts} from './useFastAccountContacts';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {AccountDetailsFragment} from '../../schema-types/AccountDetailsFragment';
import {SaltusAny} from '../../utils/SaltusAny';
import {ManageContactNotificationsModal} from './ManageContactNotifications';
import validateUUID from '../../utils/validateUUID';

export type modalFlagsType = {
  isOpen: boolean;
  type:
    | 'atr'
    | 'notification'
    | 'notifications'
    | 'factfind'
    | 'empty'
    | 'fincalc'
    | 'fpa';
};

export type ContactActionsContextType = {
  modalFlags: modalFlagsType;
  onSelect: (contactId: string, type: modalFlagsType['type']) => void;
  selectedContacts: AccountContact[];
  setSelectedContactIds: (val: string[]) => void;
  contacts: AccountContact[];
};

const ContactActionsContext = React.createContext<ContactActionsContextType>({
  modalFlags: {isOpen: false, type: 'empty'},
  onSelect: () => {},
  selectedContacts: [],
  contacts: [],
  setSelectedContactIds: () => {},
});

export const ContactActionsContextProvider: React.FC<
  PropsWithChildren<{
    accountFull?: AccountDetailsFragment | null;
  }>
> = ({accountFull: _accountFull, children}) => {
  const {account} = useAccountContext();
  const {contacts} = useFastAccountContacts();
  const {enqueueSnackbar} = useSnackbar();
  const [onGetFinCalc] = useMutation<GetFinCalcUrl, GetFinCalcUrlVariables>(gql`
    mutation GetFinCalcUrl($contactId: UUID!) {
      createFinCalcSession(contactId: $contactId) {
        sessionUrl
        ok
        errors
      }
    }
  `);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [modalFlags, setModalFlags] = useState<modalFlagsType>({
    isOpen: false,
    type: 'empty',
  });
  const closeModal = useCallback(() => {
    setModalFlags({isOpen: false, type: 'empty'});
  }, [modalFlags, setModalFlags]);

  const contextValue = useMemo(() => {
    function onSelect(contactId: string, modalType: modalFlagsType['type']) {
      setSelectedContactIds([contactId]);
      setModalFlags({isOpen: true, type: modalType});
      if (modalType === 'fincalc') {
        onGetFinCalc({
          variables: {
            contactId: validateUUID(contactId)!,
          },
        })
          .then(({data: finCalcData}) => {
            if (!finCalcData?.createFinCalcSession?.ok) {
              const message = finCalcData?.createFinCalcSession?.errors?.[0] || '';
              enqueueSnackbar(
                <Box display="flex" flexDirection="column" alignItems="baseline">
                  Error:{`${message.substring(0, 99)}...`}.<br />
                  <span>
                    Please{' '}
                    <Link
                      href="https://saltus.freshdesk.com/support/tickets/new"
                      target="_blank"
                      rel="noreferrer"
                      color="#fff"
                    >
                      click here
                    </Link>{' '}
                    to open a support ticket, copying and pasting the error message above.
                  </span>
                </Box>,
                {
                  variant: 'error',
                  autoHideDuration: null,
                }
              );
              return;
            }
            if (finCalcData?.createFinCalcSession?.sessionUrl) {
              window.open(finCalcData?.createFinCalcSession?.sessionUrl, '_blank');
            }
          })
          .finally(() => {
            setModalFlags({isOpen: false, type: 'empty'});
          });
      }
    }
    const selectedContacts =
      contacts.filter((c: {id: SaltusAny}) => selectedContactIds.indexOf(c.id) > -1) ||
      [];
    return {
      modalFlags,
      onSelect,
      selectedContacts,
      setSelectedContactIds,
      contacts,
    };
  }, [modalFlags, setSelectedContactIds, contacts]);

  if (contacts.length < 1) {
    return (
      <Grid item xs={12}>
        <Panel color={themeColors.blue} title="Contacts">
          <Box mt={2} mb={2}>
            <Loading />
          </Box>
        </Panel>
      </Grid>
    );
  }
  return (
    <ContactActionsContext.Provider value={contextValue}>
      <ShowIfDebugWrapper>
        <Box ml={3}>Debug modalFlag: {modalFlags.type}</Box>
      </ShowIfDebugWrapper>
      {children}
      {/* {modalFlags.isOpen && modalFlags.type === 'notification' ? (
          <SendPortalNotificationModal
            contacts={selectedContacts}
            isOpen={modalFlags.isOpen && modalFlags.type === 'notification'}
            close={closeModal}
          />
        ) : null} */}

      {modalFlags.isOpen && modalFlags.type === 'notifications' ? (
        <ManageContactNotificationsModal
          contact={contextValue.selectedContacts[0]}
          isOpen={modalFlags.isOpen && modalFlags.type === 'notifications'}
          close={closeModal}
        />
      ) : null}

      {modalFlags.isOpen && modalFlags.type === 'atr' ? (
        <SendAtrQuestionnaireModal
          accountId={account!.id}
          contact={contextValue.selectedContacts[0]}
          isOpen={modalFlags.isOpen && modalFlags.type === 'atr'}
          close={closeModal}
        />
      ) : null}
      {modalFlags.isOpen && modalFlags.type === 'factfind' ? (
        <SendFactFindModal
          account={account!}
          contact={contextValue.selectedContacts[0]}
          isOpen={modalFlags.isOpen && modalFlags.type === 'factfind'}
          close={closeModal}
        />
      ) : null}
      {modalFlags.isOpen && modalFlags.type === 'fpa' ? (
        <RequestFPASignatureModal
          account={account!}
          contact={contextValue.selectedContacts[0]}
          isOpen={modalFlags.isOpen && modalFlags.type === 'fpa'}
          close={closeModal}
        />
      ) : null}
    </ContactActionsContext.Provider>
  );
};

export default ContactActionsContext;
